import React from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import TextField from '@mui/material/TextField';
import CloseIcon from '@mui/icons-material/Close';
import makeStyles from '@mui/styles/makeStyles';
import Tooltip from '@mui/material/Tooltip';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import '../FilterStyles.scss';

const useChipStyles = makeStyles({
  chipRoot: {
    maxWidth: '200px',
    '&.MuiChip-colorError .ExclusionIcon': {
      background: '#d32f2f!important',
      boxShadow: '-3px 0px 0px 0rem #d32f2f!important'
    },
    '&:hover': {
      '& .MuiChip-label': {
        opacity: 1
      },
      '& .ExclusionIcon': {
        display: 'flex',
        background: 'rgb(29 119 75)',
        marginLeft: '-23px',
        zIndex: 1000,
        boxShadow: '-3px 0px 0px 0rem rgb(29 119 75)'
      },
      '& .DeleteIcon ': {
        order: 2,
        color: 'white'
      }
    },
    '& .ExclusionIcon': {
      order: 1,
      marginRight: '5px',
      cursor: 'pointer',
      display: 'none'
    },
    '& .DeleteIcon ': {
      order: 2,
      color: 'white'
    }
  }
});

interface FilterType {
  name: string;
  path: string;
  criteria: { value: string; included: boolean }[];
  options: string[];
}

interface Props {
  filter: FilterType;
  onFilterChange(value: FilterType): void;
}

const SalesNavMultiSelectFromOptions = ({ filter, onFilterChange }: Props) => {
  const chipClasses = useChipStyles();

  return (
    <>
      <Autocomplete
        size="small"
        onChange={(_e, v) => {
          onFilterChange({
            ...filter,
            criteria: v.map((option) => {
              const existing = filter.criteria.find((x) => x.value === option);
              return existing || { value: option, included: true };
            })
          });
        }}
        multiple
        options={filter.options || []}
        isOptionEqualToValue={(option, value) => option === value}
        value={filter.criteria.map((c) => c.value)}
        getOptionLabel={(option) => option}
        disableCloseOnSelect
        filterSelectedOptions
        renderTags={(value: string[]) => (
          <Box p={1} display="flex" flexWrap="wrap" gap={0.4}>
            {value.map((optionValue: string) => {
              const option = filter.criteria.find((c) => c.value === optionValue) || {
                value: optionValue,
                included: true
              };
              return (
                <Tooltip arrow placement="top" title={option.value} key={option.value}>
                  <Chip
                    classes={{ root: chipClasses.chipRoot }}
                    size="small"
                    label={option.value}
                    onDelete={() =>
                      onFilterChange({
                        ...filter,
                        criteria: filter.criteria.filter((x) => x.value !== option.value)
                      })
                    }
                    deleteIcon={<CloseIcon className="DeleteIcon" />}
                    icon={
                      option.included ? (
                        <RemoveCircleOutlineIcon className="ExclusionIcon" />
                      ) : (
                        <AddCircleOutlineIcon className="ExclusionIcon" />
                      )
                    }
                    color={option.included ? 'success' : 'error'}
                    sx={{ color: 'white' }}
                    onClick={() =>
                      onFilterChange({
                        ...filter,
                        criteria: filter.criteria.map((x) =>
                          x.value === option.value ? { ...x, included: !x.included } : x
                        )
                      })
                    }
                  />
                </Tooltip>
              );
            })}
          </Box>
        )}
        renderInput={(params) => <TextField {...params} label={filter.name} size="small" placeholder={filter.name} />}
      />
    </>
  );
};

export default SalesNavMultiSelectFromOptions;
